import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, BlockStack, InlineStack, Text, Button, Icon, Link, Box, Spinner, Badge, TextField, Tooltip } from '@shopify/polaris';
import { PlusCircleIcon, DeleteIcon, ChevronRightIcon, ChevronDownIcon, EditIcon, CheckIcon, XIcon, DragHandleIcon, InfoIcon } from '@shopify/polaris-icons';
import { CardHeading } from '../../../shared/components/cards/CardHeading.jsx';
import { getRequest } from '../../../shared/components/functions.jsx';
import ProductSelect from '../../../shared/components/productSelect.jsx';

// Format currency with European style (dot for thousands, comma for decimals)
const formatEuro = (value) => {
  const num = parseFloat(value) || 0;
  return num.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function BomTreeItem({ item, level = 0, onRemove, onUpdateQuantity, parentId, disabled, isDragging, dragHandleProps, parentAccumulatedQty = 1 }) {
  const [expanded, setExpanded] = useState(false);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingQty, setEditingQty] = useState(false);
  const [tempQty, setTempQty] = useState(String(item.ComponentQuantity || 1));
  const [isHovered, setIsHovered] = useState(false);

  const hasChildren = item.ChildCount > 0;
  const isLeaf = !hasChildren;

  // Calculate accumulated quantity through the hierarchy
  const localQty = parseFloat(item.ComponentQuantity) || 1;
  const accumulatedQty = localQty * parentAccumulatedQty;

  const loadChildren = async () => {
    if (!hasChildren || children.length > 0) return;
    setLoading(true);
    try {
      const data = await getRequest(`/api/products/${item.ProductID}/bom`);
      setChildren(data.children || []);
    } catch (err) {
      console.error('Error loading children:', err);
    }
    setLoading(false);
  };

  const handleToggle = () => {
    if (!expanded && hasChildren) {
      loadChildren();
    }
    setExpanded(!expanded);
  };

  const handleSaveQty = () => {
    const qty = parseFloat(tempQty);
    if (!isNaN(qty) && qty > 0) {
      onUpdateQuantity(item.ProductID, qty);
    }
    setEditingQty(false);
  };

  const handleCancelQty = () => {
    setTempQty(String(item.ComponentQuantity || 1));
    setEditingQty(false);
  };

  const cost = parseFloat(item.Eenheidsaankoopprijs) || 0;
  // Line cost uses accumulated quantity for the true total cost contribution
  const lineCost = cost * accumulatedQty;

  // Calculate indentation: 24px per level for clear visual hierarchy
  const indentPx = level * 24;

  return (
    <Box paddingBlockStart={level > 0 ? '100' : '0'}>
      {/* Outer wrapper for indentation and tree line */}
      <div
        style={{
          marginLeft: `${indentPx}px`,
          borderLeft: level > 0 ? '2px solid #e1e3e5' : 'none',
          paddingLeft: level > 0 ? '12px' : '0'
        }}
      >
        {/* Row content with hover effect - separate from children */}
        <div
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{
            padding: '4px 8px',
            background: isDragging ? '#e8e8e8' : isHovered ? '#f1f2f3' : undefined,
            borderRadius: '4px',
            transition: 'background-color 0.15s ease',
            cursor: 'default'
          }}
        >
          <InlineStack align="space-between" blockAlign="center" wrap={false}>
            <InlineStack gap="200" blockAlign="center" wrap={false}>
              {/* Drag handle - only for top level */}
              {level === 0 && dragHandleProps && (
                <div {...dragHandleProps} style={{ cursor: 'grab', padding: '4px' }}>
                  <Icon source={DragHandleIcon} tone="subdued" />
                </div>
              )}
              {hasChildren ? (
                <div style={{ cursor: 'pointer' }} onClick={handleToggle}>
                  <Icon source={expanded ? ChevronDownIcon : ChevronRightIcon} tone="subdued" />
                </div>
              ) : (
                <div style={{ width: '20px' }} />
              )}
              <BlockStack gap="050">
                <InlineStack gap="200" blockAlign="center">
                  <Link url={`/products/${item.ProductID}`} removeUnderline>
                    <Text as="span" fontWeight="semibold">
                      #{item.ProductID} - {item.ProductNameNL}
                    </Text>
                  </Link>
                  {item.Discontinued && <Badge tone="critical">Discontinued</Badge>}
                  {/* Show badge indicating if this is counted in cost or not */}
                  {hasChildren && <Badge tone="attention">Assembly</Badge>}
                </InlineStack>
                <Text as="span" tone="subdued" variant="bodySm">
                  {item.Productref} {item.SupplierName ? `| ${item.SupplierName}` : ''}
                </Text>
              </BlockStack>
            </InlineStack>
            <InlineStack gap="200" blockAlign="center">
              {/* Quantity display/edit */}
              {level === 0 && (
                <>
                  {editingQty ? (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleSaveQty();
                      }}
                      style={{ display: 'inline' }}
                    >
                      <InlineStack gap="100" blockAlign="center">
                        <div style={{ width: '80px' }}>
                          <TextField type="number" value={tempQty} onChange={setTempQty} autoComplete="off" min="0.01" step="0.01" selectTextOnFocus autoFocus />
                        </div>
                        <Button icon={CheckIcon} variant="plain" submit accessibilityLabel="Save quantity" />
                        <Button icon={XIcon} variant="plain" onClick={handleCancelQty} accessibilityLabel="Cancel" />
                      </InlineStack>
                    </form>
                  ) : (
                    <InlineStack gap="100" blockAlign="center">
                      <Text as="span" tone="subdued">
                        {localQty} x
                      </Text>
                      <Button icon={EditIcon} variant="plain" onClick={() => setEditingQty(true)} disabled={disabled} accessibilityLabel="Edit quantity" />
                    </InlineStack>
                  )}
                </>
              )}
              {/* Nested items: show local qty and accumulated total */}
              {level > 0 && (
                <div style={{ minWidth: '70px', textAlign: 'right' }}>
                  <BlockStack gap="0">
                    <Text as="span" tone="subdued">
                      {localQty} x
                    </Text>
                    {parentAccumulatedQty !== 1 && (
                      <Text as="span" tone="subdued" variant="bodySm">
                        (={accumulatedQty} total)
                      </Text>
                    )}
                  </BlockStack>
                </div>
              )}
              {/* Cost display - show strikethrough for assemblies since they're not counted */}
              <div style={{ minWidth: '120px', textAlign: 'right' }}>
                <BlockStack gap="0">
                  <Text as="span" tone="subdued" variant="bodySm">
                    €{formatEuro(cost)} / unit
                  </Text>
                  {hasChildren ? (
                    <Text as="span" fontWeight="semibold" tone="subdued">
                      <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>€{formatEuro(lineCost)}</span>
                    </Text>
                  ) : (
                    <Text as="span" fontWeight="semibold" tone="success">
                      €{formatEuro(lineCost)}
                    </Text>
                  )}
                </BlockStack>
              </div>
              {level === 0 && <Button icon={DeleteIcon} variant="plain" tone="critical" onClick={() => onRemove(item.ProductID)} disabled={disabled} accessibilityLabel="Remove from BOM" />}
            </InlineStack>
          </InlineStack>
        </div>

        {/* Loading and children outside the hover div */}
        {loading && (
          <Box paddingInlineStart="600" paddingBlockStart="200">
            <Spinner size="small" />
          </Box>
        )}

        {expanded && children.length > 0 && (
          <Box paddingBlockStart="100">
            {children.map((child) => (
              <BomTreeItem key={child.ProductID} item={child} level={level + 1} parentId={item.ProductID} disabled={disabled} parentAccumulatedQty={accumulatedQty} />
            ))}
          </Box>
        )}
      </div>
    </Box>
  );
}

// Draggable wrapper component
function DraggableBomItem({ item, index, onRemove, onUpdateQuantity, parentId, disabled, onDragStart, onDragOver, onDragEnd, onDrop, draggedIndex }) {
  const ref = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragStart = (e) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    onDragStart(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsDragOver(true);
    onDragOver(index);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    onDrop(index);
  };

  const handleDragEnd = () => {
    onDragEnd();
  };

  const isDragging = draggedIndex === index;

  return (
    <div
      ref={ref}
      draggable
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onDragEnd={handleDragEnd}
      style={{
        opacity: isDragging ? 0.5 : 1,
        borderTop: isDragOver && draggedIndex !== index ? '2px solid #2c6ecb' : '2px solid transparent',
        transition: 'border-color 0.15s ease',
        paddingTop: '4px',
        paddingBottom: '4px'
      }}
    >
      <BomTreeItem
        item={item}
        level={0}
        onRemove={onRemove}
        onUpdateQuantity={onUpdateQuantity}
        parentId={parentId}
        disabled={disabled}
        isDragging={isDragging}
        dragHandleProps={{
          onMouseDown: (e) => e.stopPropagation()
        }}
      />
    </div>
  );
}

export default function BomCard({ productId, disabled }) {
  const [bomData, setBomData] = useState({ children: [], parents: [], parent: null });
  const [costData, setCostData] = useState({ ownCost: 0, componentCost: 0, totalCost: 0, totalComponents: 0, leafComponents: 0 });
  const [productData, setProductData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState('1');
  const [saving, setSaving] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const fetchBomData = useCallback(async () => {
    if (!productId || productId === 'new') return;
    setLoading(true);
    try {
      const [bom, cost, product] = await Promise.all([getRequest(`/api/products/${productId}/bom`), getRequest(`/api/products/${productId}/bom/cost`), getRequest(`/api/products/${productId}`)]);
      setBomData(bom);
      setCostData(cost);
      setProductData(product);
    } catch (err) {
      console.error('Error fetching BOM data:', err);
    }
    setLoading(false);
  }, [productId]);

  useEffect(() => {
    fetchBomData();
  }, [fetchBomData]);

  const handleAddComponent = async () => {
    if (!selectedProduct) return;
    const qty = parseFloat(quantity) || 1;
    setSaving(true);
    try {
      const response = await fetch(`/api/products/${productId}/bom`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ childProductId: selectedProduct, quantity: qty })
      });

      if (response.ok) {
        setSelectedProduct(null);
        setQuantity('1');
        setAdding(false);
        await fetchBomData();
      } else {
        const error = await response.json();
        alert(error.msg || 'Error adding component');
      }
    } catch (err) {
      console.error('Error adding component:', err);
      alert('Error adding component');
    }
    setSaving(false);
  };

  const handleUpdateQuantity = async (childId, newQuantity) => {
    setSaving(true);
    try {
      const response = await fetch(`/api/products/${productId}/bom/${childId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity: newQuantity })
      });

      if (response.ok) {
        await fetchBomData();
      } else {
        const error = await response.json();
        alert(error.msg || 'Error updating quantity');
      }
    } catch (err) {
      console.error('Error updating quantity:', err);
      alert('Error updating quantity');
    }
    setSaving(false);
  };

  const handleRemoveComponent = async (childId) => {
    if (!confirm('Remove this component from the BOM?')) return;
    setSaving(true);
    try {
      const response = await fetch(`/api/products/${productId}/bom/${childId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await fetchBomData();
      } else {
        const error = await response.json();
        alert(error.msg || 'Error removing component');
      }
    } catch (err) {
      console.error('Error removing component:', err);
      alert('Error removing component');
    }
    setSaving(false);
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (index) => {
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = async (dropIndex) => {
    // No change needed if dropping in same position or if last item dropped at end
    if (draggedIndex === null || draggedIndex === dropIndex || (draggedIndex === bomData.children.length - 1 && dropIndex === bomData.children.length)) {
      handleDragEnd();
      return;
    }

    // Reorder the array
    const newChildren = [...bomData.children];
    const [draggedItem] = newChildren.splice(draggedIndex, 1);
    newChildren.splice(dropIndex, 0, draggedItem);

    // Update local state immediately for responsive UI
    setBomData({ ...bomData, children: newChildren });
    handleDragEnd();

    // Persist the new order
    setSaving(true);
    try {
      const orderedIds = newChildren.map((child) => child.ProductID);
      const response = await fetch(`/api/products/${productId}/bom/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderedIds })
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Error reordering:', error);
        // Revert on error
        await fetchBomData();
      }
    } catch (err) {
      console.error('Error reordering components:', err);
      await fetchBomData();
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <Card>
        <CardHeading title="Bill of Materials (BOM)" />
        <Box padding="400">
          <InlineStack align="center">
            <Spinner size="small" />
          </InlineStack>
        </Box>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeading title="Bill of Materials (BOM)" />
      <BlockStack gap="400">
        {/* Parent products info - a product can belong to multiple BOMs */}
        {bomData.parents && bomData.parents.length > 0 && (
          <Box background="bg-surface-secondary" padding="300" borderRadius="200">
            <BlockStack gap="200">
              <Text as="span" tone="subdued" variant="bodySm">
                This product is a component of:
              </Text>
              {bomData.parents.map((parent) => (
                <InlineStack key={parent.ProductID} gap="200" blockAlign="center">
                  <Link url={`/products/${parent.ProductID}`} removeUnderline>
                    <Text as="span" fontWeight="semibold">
                      #{parent.ProductID} - {parent.ProductNameNL}
                    </Text>
                  </Link>
                  {parent.Productref && (
                    <Text as="span" tone="subdued" variant="bodySm">
                      ({parent.Productref})
                    </Text>
                  )}
                </InlineStack>
              ))}
            </BlockStack>
          </Box>
        )}

        {/* Cost summary */}
        {(costData.totalComponents > 0 || bomData.children.length > 0) && (
          <Box background="bg-surface-success" padding="300" borderRadius="200">
            <BlockStack gap="200">
              <InlineStack gap="100" blockAlign="center">
                <Text as="span" fontWeight="semibold">
                  Cost Roll-up
                </Text>
                <Tooltip content="Only leaf parts (green) are counted. Assemblies are skipped to avoid double-counting. Nested quantities are multiplied through the hierarchy.">
                  <div style={{ cursor: 'help' }}>
                    <Icon source={InfoIcon} tone="subdued" />
                  </div>
                </Tooltip>
              </InlineStack>
              <InlineStack align="space-between">
                <Text as="span" fontWeight="bold">
                  Components ({costData.leafComponents} leaf parts):
                </Text>
                <Text as="span" fontWeight="bold">
                  €{formatEuro(costData.componentCost)}
                </Text>
              </InlineStack>
              {productData?.RolledUpPurchasePrice != null && (
                <InlineStack align="space-between">
                  <Text as="span" tone="subdued" variant="bodySm">
                    Stored rolled-up cost:
                  </Text>
                  <Text as="span" tone="subdued" variant="bodySm">
                    €{formatEuro(productData.RolledUpPurchasePrice)}
                  </Text>
                </InlineStack>
              )}
              <Box borderBlockStartWidth="025" borderColor="border" paddingBlockStart="200">
                <InlineStack align="space-between">
                  <Text as="span" tone="subdued">
                    Own purchase price (for comparison):
                  </Text>
                  <Text as="span" tone="subdued">
                    €{formatEuro(costData.ownCost)}
                  </Text>
                </InlineStack>
              </Box>
            </BlockStack>
          </Box>
        )}

        {/* Components list */}
        {bomData.children.length > 0 ? (
          <BlockStack gap="100">
            <InlineStack align="space-between">
              <InlineStack gap="200">
                <Text as="span" fontWeight="semibold">
                  Components
                </Text>
                <Text as="span" tone="subdued" variant="bodySm">
                  (drag to reorder)
                </Text>
              </InlineStack>
              <Text as="span" tone="subdued" variant="bodySm">
                Unit / Line
              </Text>
            </InlineStack>
            {bomData.children.map((child, index) => (
              <DraggableBomItem
                key={child.ProductID}
                item={child}
                index={index}
                onRemove={handleRemoveComponent}
                onUpdateQuantity={handleUpdateQuantity}
                parentId={productId}
                disabled={disabled || saving}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
                onDrop={handleDrop}
                draggedIndex={draggedIndex}
              />
            ))}
            {/* Drop zone for dropping at the end of the list */}
            {draggedIndex !== null && (
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  handleDrop(bomData.children.length);
                }}
                style={{
                  height: '40px',
                  borderTop: '2px dashed #ccc',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: '8px',
                  backgroundColor: '#f9f9f9'
                }}
              >
                <Text as="span" tone="subdued" variant="bodySm">
                  Drop here to move to end
                </Text>
              </div>
            )}
          </BlockStack>
        ) : (
          <Text as="p" tone="subdued">
            No components defined for this product.
          </Text>
        )}

        {/* Add component section */}
        {adding ? (
          <Box background="bg-surface-secondary" padding="300" borderRadius="200">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (selectedProduct && !saving) {
                  handleAddComponent();
                }
              }}
            >
              <BlockStack gap="300">
                <Text as="span" fontWeight="semibold">
                  Add Component
                </Text>
                <Text as="span" tone="subdued" variant="bodySm">
                  Search by name, ID, @supplier, or #reference
                </Text>
                <ProductSelect value={selectedProduct} onChange={setSelectedProduct} disabled={saving} extended />
                <TextField label="Quantity" type="number" value={quantity} onChange={setQuantity} autoComplete="off" min="0.01" step="0.01" />
                <InlineStack gap="200">
                  <Button submit disabled={!selectedProduct || saving} loading={saving} variant="primary">
                    Add
                  </Button>
                  <Button
                    onClick={() => {
                      setAdding(false);
                      setSelectedProduct(null);
                      setQuantity('1');
                    }}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                </InlineStack>
              </BlockStack>
            </form>
          </Box>
        ) : (
          <Button icon={PlusCircleIcon} onClick={() => setAdding(true)} disabled={disabled}>
            Add component
          </Button>
        )}
      </BlockStack>
    </Card>
  );
}
